// tourApi 모듈
// - 공공 관광 API(한국관광공사) 호출을 위한 헬퍼들을 제공합니다.
// - 클라이언트는 로컬/프록시(worker) URL을 통해 요청을 보냅니다. 민감한 키는 서버/워커에서 관리해야 합니다.

// 개발 환경에서는 로컬 서버를, 프로덕션에서는 원격 서버를 사용
const isLocalDev = import.meta.env.DEV;

// API 기본 URL (로컬 개발 시에는 프록시를 통해 요청)
const BASE_URL = isLocalDev 
  ? 'http://localhost:8787'  // 로컬 개발 서버 (프록시)
  : 'https://apis.data.go.kr/B551011';  // 프로덕션 서버

// 클라이언트에서 호출하는 프록시 기본 URL
// 개발 환경에서는 로컬 서버를, 프로덕션에서는 환경 변수 또는 기본 URL 사용
const WORKER_URL = isLocalDev
  ? 'http://localhost:8787'
  : ((import.meta as any)?.env?.VITE_TOUR_WORKER_URL as string) || 'https://tour-api-proxy.lsd9901.workers.dev';

// API 요청에 사용할 기본 파라미터
const DEFAULT_PARAMS = {
  MobileOS: 'WEB',
  MobileApp: 'SoraMiro',
  _type: 'json',
  serviceKey: isLocalDev ? '' : undefined, // 로컬 개발 시에는 프록시가 처리
};

// 언어별 서비스명 매핑 (공공 API의 서비스 식별자)
const API_SERVICES = {
ko: 'KorService2',
en: 'EngService2',
ja: 'JpnService2',
zh: 'ChsService2',
de: 'GerService2',
fr: 'FreService2'
} as const;

export type Language = keyof typeof API_SERVICES;

/**
* TourApiParams
* - 공공 API에 넘기는 쿼리 파라미터들을 타입으로 정의합니다.
* - 필요한 경우 추가 필드를 여기에 선언하세요.
*/
export interface TourApiParams {
serviceKey?: string;
numOfRows?: number;
pageNo?: number;
// MobileOS 값: IOS(아이폰), AND(안드로이드), WEB(웹), ETC(기타)
MobileOS?: 'ETC' | 'IOS' | 'AND' | 'WEB';
MobileApp?: string;
_type?: 'json' | 'xml';
listYN?: 'Y' | 'N';
arrange?: 'A' | 'B' | 'C' | 'D' | 'E' | 'O' | 'Q' | 'R' | 'S';
contentTypeId?: string;
// 법정동 코드 (v4.3 이후 추가)
lDongRegnCd?: string;  // 법정동 시도 코드
lDongSignguCd?: string;  // 법정동 시군구 코드 (lDongRegnCd 필수)
// 분류체계 (v4.3 이후 추가)
lclsSystm1?: string;  // 분류체계 대분류
lclsSystm2?: string;  // 분류체계 중분류 (lclsSystm1 필수)
lclsSystm3?: string;  // 분류체계 소분류 (lclsSystm1, lclsSystm2 필수)
modifiedtime?: string;  // 콘텐츠 수정일 (YYYYMMDD 형식)
keyword?: string;
contentId?: string;
mapX?: string;
mapY?: string;
radius?: number;
defaultYN?: 'Y' | 'N';
firstImageYN?: 'Y' | 'N';
addrinfoYN?: 'Y' | 'N';
mapinfoYN?: 'Y' | 'N';
overviewYN?: 'Y' | 'N';
imageYN?: 'Y' | 'N';
// 축제/행사 파라미터 (YYYYMMDD 형식)
eventStartDate?: string;
eventEndDate?: string;
}

/**
* TourApiResponse
* - 공공 API의 공통 응답 래퍼 구조를 표현합니다.
* - 실제 item 타입은 제네릭으로 전달합니다.
*/
export interface TourApiResponse<T = any> {
response: {
header: {
resultCode: string;
resultMsg: string;
};
body: {
items?: {
item: T[];
};
numOfRows: number;
pageNo: number;
totalCount: number;
};
};
}

// 인메모리 캐시 (개발 환경 HMR 시 유지되도록 globalThis에 저장)
const CACHE_TTL_MS = 5 * 60 * 1000; // 5분
if (!(globalThis as any).__tourApiCache) {
  (globalThis as any).__tourApiCache = new Map<string, { expiry: number; data: any }>();
}
const cache: Map<string, { expiry: number; data: any }> = (globalThis as any).__tourApiCache;

/**
 * fetchWithRetry
 * - 지정한 타임아웃과 재시도 수를 사용해 fetch를 수행합니다.
 * - 4xx 응답은 재시도하지 않으며, 타임아웃 발생 시 AbortController로 요청을 취소합니다.
 * @param url 요청 URL
 * @param options fetch 옵션
 * @param timeoutMs 타임아웃(ms)
 * @param retries 재시도 횟수
 */
async function fetchWithRetry(url: string, options: RequestInit = {}, timeoutMs = 10000, retries = 1): Promise<Response> {
  const attempt = async (n: number): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const resp = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      if (!resp.ok) {
        // 4xx 에러의 경우 재시도하지 않음
        if (resp.status >= 400 && resp.status < 500) {
          return resp;
        }
        throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
      }
      return resp;
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        // 타임아웃으로 처리
        if (n <= 0) throw new Error('Request timed out');
      }
      if (n <= 0) throw err;
      // 지수 백오프
      const backoff = 200 * Math.pow(2, retries - n);
      await new Promise((res) => setTimeout(res, backoff));
      return attempt(n - 1);
    }
  };

  return attempt(retries + 1);
}

/**
 * callTourApi
 * - 언어별 서비스 엔드포인트에 요청을 보내고, 공통 응답 타입으로 반환합니다.
 * - 내부적으로 캐시 확인, fetchWithRetry 호출, 에러 표준화 처리를 수행합니다.
 * @param language 사용할 언어(서비스)
 * @param endpoint 서비스 엔드포인트 (예: '/areaBasedList2')
 * @param params API 파라미터
 */
export async function callTourApi<T>(
  language: Language,
  endpoint: string,
  params: TourApiParams = {}
): Promise<TourApiResponse<T>> {
  const serviceName = API_SERVICES[language];
  if (!serviceName) {
    throw new Error(`Unsupported language: ${language}`);
  }

  // 기본 파라미터 설정 (웹에서는 MobileOS를 WEB으로 고정)
  const defaultParams: TourApiParams = {
    MobileOS: 'WEB',
    MobileApp: 'SoraMiro',
    _type: 'json',
    ...params,
  };

  // URLSearchParams를 사용하여 쿼리 파라미터 생성
  const queryParams = new URLSearchParams();
  Object.entries(defaultParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // 배열인 경우 각 요소를 반복하여 추가
      if (Array.isArray(value)) {
        value.forEach(v => queryParams.append(key, v.toString()));
      } else {
        queryParams.append(key, value.toString());
      }
    }
  });

  // 엔드포인트 앞에 슬래시가 있으면 제거
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
  const url = new URL(`${serviceName}/${cleanEndpoint}`, WORKER_URL);

  // 쿼리 파라미터 추가
  queryParams.forEach((value, key) => {
    url.searchParams.append(key, value);
  });

  // 🔍 디버깅: URL 로그 출력
  console.log('🌐 API 요청 URL:', url.toString());
  console.log('📦 전달된 파라미터:', defaultParams);

  // 캐시 확인
  const cacheKey = url.toString();
  const now = Date.now();
  const cached = cache.get(cacheKey);
  if (cached && cached.expiry > now) {
    return cached.data as TourApiResponse<T>;
  }
  // --- fetch 호출 (재시도 포함) ---
  const DEFAULT_TIMEOUT_MS = 10000; // 10초
  const fetchOptions: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json'
    }
  };

  try {
    const response = await fetchWithRetry(url.toString(), fetchOptions, DEFAULT_TIMEOUT_MS, 2);
    if (!response.ok) {
      // JSON 에러 본문이 있는 경우 파싱 시도
      let bodyText: string | undefined;
      try {
        bodyText = await response.text();
      } catch {}
      const err = new Error(`API request failed with status ${response.status}: ${bodyText || response.statusText}`);
      (err as any).status = response.status;
      (err as any).url = url.toString();
      throw err;
    }

    const json = await response.json();

    // 성공 응답 캐싱
    try {
      cache.set(cacheKey, { expiry: Date.now() + CACHE_TTL_MS, data: json });
    } catch (e) {
      console.warn('Failed to cache API response', e);
    }
 
    return json;
  } catch (error: any) {
    // 컨텍스트와 함께 오류 객체 표준화
    if (error.name === 'AbortError' || /timed out/i.test(error.message || '')) {
      const e = new Error('API 요청이 타임아웃되었습니다. 네트워크 상태를 확인하세요.');
      (e as any).original = error;
      (e as any).url = url.toString();
      throw e;
    }
    const e = new Error(error.message || 'API 호출 실패');
    (e as any).original = error;
    (e as any).url = url.toString();
    throw e;
  }
}

// 지역기반 관광정보 조회
export async function getAreaBasedList(
  language: Language,
  params: TourApiParams = {}
): Promise<TourApiResponse> {
  return callTourApi(language, '/areaBasedList2', params);
}

// 키워드 검색
export async function searchKeyword(
  language: Language,
  keyword: string,
  params: TourApiParams = {}
): Promise<TourApiResponse> {
  return callTourApi(language, '/searchKeyword2', {
    ...params,
    keyword
  });
}

// 법정동 코드 조회 (v4.3 이후)
export async function getLdongCode(
  language: Language,
  lDongRegnCd?: string
): Promise<TourApiResponse> {
  return callTourApi(language, '/ldongCode2', {
    lDongRegnCd,
    numOfRows: 100
  });
}

// 분류체계 코드 조회 (v4.3 이후)
export async function getLclsSystmCode(
  language: Language,
  lclsSystm1?: string,
  lclsSystm2?: string
): Promise<TourApiResponse> {
  return callTourApi(language, '/lclsSystmCode2', {
    lclsSystm1,
    lclsSystm2,
    numOfRows: 100
  });
}

// 공통정보 상세조회
export async function getDetailCommon(
  language: Language,
  contentId: string,
  contentTypeId?: string
): Promise<TourApiResponse> {
  return callTourApi(language, '/detailCommon2', {
    contentId
  });
}

// 소개정보 조회
export async function getDetailIntro(
  language: Language,
  contentId: string,
  contentTypeId: string
): Promise<TourApiResponse> {
  return callTourApi(language, '/detailIntro2', {
    contentId,
    contentTypeId
  });
}

// 반복정보 조회 (상세정보)
export async function getDetailInfo(
  language: Language,
  contentId: string,
  contentTypeId: string
): Promise<TourApiResponse> {
  return callTourApi(language, '/detailInfo2', {
    contentId,
    contentTypeId
  });
}

// 이미지정보 조회
export async function getDetailImage(
  language: Language,
  contentId: string
): Promise<TourApiResponse> {
  return callTourApi(language, '/detailImage2', {
    contentId,
    imageYN: 'Y',
    numOfRows: 20
  });
}

// 행사정보 조회
export async function searchFestival(
  language: Language,
  params: TourApiParams = {}
): Promise<TourApiResponse> {
  return callTourApi(language, '/searchFestival2', params);
}

// 숙박정보 조회  
export async function searchStay(
  language: Language,
  params: TourApiParams = {}
): Promise<TourApiResponse> {
  return callTourApi(language, '/searchStay2', params);
}

// 위치기반 관광정보 조회
export async function getLocationBasedList(
  language: Language,
  mapX: string,
  mapY: string,
  radius: number = 1000,
  params: TourApiParams = {}
): Promise<TourApiResponse> {
  return callTourApi(language, '/locationBasedList2', {
    ...params,
    mapX,
    mapY,
    radius
  });
}

/**
 * 반려동물 관광정보 조회
 * @param language 언어 코드
 * @param contentId 콘텐츠 ID
 * @param params 추가 파라미터 (선택사항)
 */
export async function getDetailPetTour(
  language: Language,
  contentId: string,
  params: TourApiParams = {}
): Promise<TourApiResponse> {
  return callTourApi(language, '/detailPetTour2', {
    ...params,
    contentId
  });
}

/**
 * 법정동 코드 (v4.4부터 지역 조회는 법정동 코드 사용)
 * 이전의 areaCode/sigunguCode 대신 lDongRegnCd를 사용
 */
export const LDONG_CODES = {
  seoul: '11',       // 서울특별시
  busan: '26',       // 부산광역시
  daegu: '27',       // 대구광역시
  incheon: '28',     // 인천광역시
  gwangju: '29',     // 광주광역시
  daejeon: '30',     // 대전광역시
  ulsan: '31',       // 울산광역시
  gyeonggi: '41',    // 경기도
  gangwon: '42',     // 강원도
  chungbuk: '43',    // 충청북도
  chungnam: '44',    // 충청남도
  jeonbuk: '45',     // 전라북도
  jeonnam: '46',     // 전라남도
  gyeongbuk: '47',   // 경상북도
  gyeongnam: '48',   // 경상남도
  jeju: '50'         // 제주특별자치도
};

// 숫자 코드 기반 지역 코드 맵핑 (UI의 region값이 '11', '26' 등의 숫자 문자열이므로)
export const AREA_CODES = {
  'all': undefined,
  '11': '11',        // 서울특별시
  '26': '26',        // 부산광역시
  '27': '27',        // 대구광역시
  '28': '28',        // 인천광역시
  '29': '29',        // 광주광역시
  '30': '30',        // 대전광역시
  '31': '31',        // 울산광역시
  '41': '41',        // 경기도
  '42': '42',        // 강원도
  '43': '43',        // 충청북도
  '44': '44',        // 충청남도
  '45': '45',        // 전라북도
  '46': '46',        // 전라남도
  '47': '47',        // 경상북도
  '48': '48',        // 경상남도
  '50': '50'         // 제주특별자치도
} as const;

// 콘텐츠 타입 코드
export const CONTENT_TYPE_CODES = {
  tourist_spot: '12',      // 관광지
  cultural_facility: '14', // 문화시설
  festival: '15',          // 행사/공연/축제
  travel_course: '25',     // 여행코스
  leports: '28',          // 레포츠
  accommodation: '32',     // 숙박
  shopping: '38',         // 쇼핑
  restaurant: '39'        // 음식점
} as const;

/**
 * 분류체계 대분류 코드 (v4.3 이후)
 * 분류체계는 3단계 구조: 대분류(lclsSystm1) -> 중분류(lclsSystm2) -> 소분류(lclsSystm3)
 */
export const CLASSIFICATION_SYSTEM = {
  // 숙박 (AC)
  AC: { name: '숙박', contentTypeId: '32' },
  // 축제/공연/행사 (EV)
  EV: { name: '축제/공연/행사', contentTypeId: '15' },
  // 체험관광 (EX)
  EX: { name: '체험관광', contentTypeId: '12' },
  // 음식 (FD)
  FD: { name: '음식', contentTypeId: '39' },
  // 역사관광 (HS)
  HS: { name: '역사관광', contentTypeId: '12' },
  // 레저스포츠 (LS)
  LS: { name: '레저스포츠', contentTypeId: '28' },
  // 자연관광 (NA)
  NA: { name: '자연관광', contentTypeId: '12' },
  // 쇼핑 (SH)
  SH: { name: '쇼핑', contentTypeId: '38' },
  // 문화관광 (VE)
  VE: { name: '문화관광', contentTypeId: '12' },
  // 추천코스 (C01)
  C01: { name: '추천코스', contentTypeId: '25' }
} as const;